import { faArrowUpWideShort, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { faClose, faCommentDots } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { api } from 'src/utils/api';
import { API_RESOURCES } from 'src/utils/constants';
import { getUuid } from 'src/utils/url';

import NewCommentForm from './new-comment-form';
import SingleComment from './single-comment';

const CommentsContainer = ({ piecesByPrintUri, user, run }) => {
  const commentRef = useRef();
  const [showComments, setShowComments] = useState(false);
  const [commentsOrder, setCommentsOrder] = useState({
    order: 'desc',
    orderBy: 'created_at',
    verbose: 'Oldest First',
  });
  const [comments, setComments] = useState([]);
  const [hideOrderRelatedComments, setHideOrderRelatedComments] = useState(true);
  const nonOrderComments = comments.filter(comment => comment.related_table_name !== 'order');

  useEffect(() => {
    async function fetchData(commentRelatedUris) {
      try {
        const result = await api
          .get(`${API_RESOURCES.COMMENT}/`, {
            searchParams: {
              'filter[related_uuid]': commentRelatedUris.map(uri => getUuid(uri)),
              sort: commentsOrder.order === 'desc' ? 'created' : '-created',
            },
          })
          .json();
        setComments(result?.resources);
      } catch (err) {
        console.error(err);
      }
    }

    if (piecesByPrintUri) {
      const commentRelatedUris = [run.uri];
      const printUris = Object.keys(piecesByPrintUri);
      const pieces = printUris.map(uri => piecesByPrintUri[uri]).filter(Boolean);
      pieces.forEach(piece => {
        if (!commentRelatedUris.includes(piece.order)) {
          commentRelatedUris.push(piece.order);
        }
      });
      fetchData(commentRelatedUris);
    }
  }, [piecesByPrintUri, commentsOrder]);

  const addNewComments = newComment => {
    setComments([...comments, newComment]);
  };

  useEffect(() => {
    const clickOutside = e => {
      if (commentRef.current && !commentRef.current.contains(e.target)) {
        setShowComments(false);
      }
    };

    document.addEventListener('mousedown', clickOutside);
    return () => document.removeEventListener('mousedown', clickOutside);
  }, []);

  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    if (!showComments || !isMobile) {
      return undefined;
    }

    const scrollY = window.scrollY;
    document.documentElement.classList.add('no-scroll-mobile');
    document.body.classList.add('no-scroll-mobile');
    document.body.style.top = `-${scrollY}px`;

    return () => {
      document.documentElement.classList.remove('no-scroll-mobile');
      document.body.classList.remove('no-scroll-mobile');
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    };
  }, [showComments]);

  return (
    <>
      <div className='comments-icon' onClick={() => setShowComments(!showComments)}>
        <FontAwesomeIcon icon={faCommentDots} size='2x' />
      </div>
      {showComments && (
        <div ref={commentRef} className='comments-container'>
          <div className='comments-content'>
            <div className='comments-container__header'>
              <div className='d-flex w-100 justify-content-between mb-2'>
                <h4 className='comments-container__header__title mb-0'>Comments</h4>
                <button
                  type='button'
                  className='border-0 bg-transparent'
                  onClick={() => setShowComments(false)}
                >
                  <FontAwesomeIcon icon={faClose} size='lg' />
                </button>
              </div>
              <Dropdown alignRight className='comments-container__header-content'>
                {hideOrderRelatedComments ? (
                  <button
                    type='button'
                    className='btn-custom btn-custom--primary btn-custom--small'
                    onClick={() => setHideOrderRelatedComments(prev => !prev)}
                  >
                    <FontAwesomeIcon icon={faEye} className='spacer-right' />
                    Show Order Comments ({Math.abs(nonOrderComments.length - comments.length)})
                  </button>
                ) : (
                  <button
                    type='button'
                    className='btn-custom btn-custom--primary btn-custom--small'
                    onClick={() => setHideOrderRelatedComments(prev => !prev)}
                  >
                    <FontAwesomeIcon icon={faEyeSlash} className='spacer-right' />
                    Hide Order Comments
                  </button>
                )}

                <Dropdown.Toggle variant='secondary' id='dropdown-basic'>
                  <FontAwesomeIcon icon={faArrowUpWideShort} className='spacer-right' />
                  Sort By
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    active={commentsOrder?.verbose === 'Newest First'}
                    onClick={() =>
                      setCommentsOrder({
                        order: 'asc',
                        orderBy: 'created_at',
                        verbose: 'Newest First',
                      })
                    }
                  >
                    Newest First
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={commentsOrder?.verbose === 'Oldest First'}
                    onClick={() =>
                      setCommentsOrder({
                        order: 'desc',
                        orderBy: 'created_at',
                        verbose: 'Oldest First',
                      })
                    }
                  >
                    Oldest First
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className='comments-container__body'>
              {hideOrderRelatedComments
                ? nonOrderComments.map(comment => (
                    // eslint-disable-next-line prettier/prettier
                  <SingleComment key={comment.uri} comment={comment} />
                  ))
                : comments.map(comment => <SingleComment key={comment.uri} comment={comment} />)}
            </div>
            <NewCommentForm user={user} updateComments={addNewComments} runUri={run.uri} />
          </div>
        </div>
      )}
    </>
  );
};

CommentsContainer.propTypes = {
  piecesByPrintUri: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  user: PropTypes.shape({}),
  run: PropTypes.shape({
    orders: PropTypes.arrayOf(PropTypes.string),
    uri: PropTypes.string.isRequired,
  }),
};

export default CommentsContainer;
